import { useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Grid,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { TrendingUp, BatteryChargingFull } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useGetBatteriesForEfficiencyQuery } from '../../../redux/api/batteriesApi';
import { ScooterBattery, BatteryEfficiencyStats } from '../../../resources/types/batteriesTypes';
import { TableCell as MuiTableCell } from '@mui/material';
import moment from 'moment';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'light'
        ? 'rgba(0, 0, 0, 0.04)'
        : 'rgba(255, 255, 255, 0.08)'
  },
  transition: 'background-color 0.2s ease'
}));

const StyledTableCell = styled(MuiTableCell)(({ theme }) => ({
  borderBottom: `1px solid ${
    theme.palette.mode === 'light'
      ? 'rgba(0, 0, 0, 0.08)'
      : 'rgba(255, 255, 255, 0.1)'
  }`,
  padding: '12px 16px'
}));

const TableHeaderCell = styled(MuiTableCell)(({ theme }) => ({
  fontWeight: 700,
  backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
  borderBottom: `2px solid ${
    theme.palette.mode === 'light'
      ? 'rgba(0, 0, 0, 0.12)'
      : 'rgba(255, 255, 255, 0.12)'
  }`,
  padding: '12px 16px'
}));

const StatCard = styled(Paper)(({ theme }) => ({
  padding: '20px 24px',
  borderRadius: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: 8
}));

const EfficiencyTab = () => {
  const { t } = useTranslation();
  const { data: batteriesData, isLoading } = useGetBatteriesForEfficiencyQuery();

  const stats: BatteryEfficiencyStats = useMemo(() => {
    if (!batteriesData?.data || batteriesData.data.length === 0) {
      return {
        total: 0,
        avgMileage: 0,
        avgEfficiency: 0,
        topPerformer: null
      };
    }

    const result = batteriesData.data.map(battery => ({
      ...battery,
      efficiency: 0
    }));

    let sumMileage = 0;
    let sumEfficiency = 0;
    let best: ScooterBattery | null = null;

    for (const row of result) {
      const spentPercent = 100 - row.chargeLevel;
      row.efficiency = spentPercent > 0 ? row.mileageSinceCharge / spentPercent : 0;

      sumMileage += row.mileageSinceCharge;
      sumEfficiency += row.efficiency;

      if (!best || row.efficiency > (best.efficiency || 0)) {
        best = row;
      }
    }

    return {
      total: result.length,
      avgMileage: +(sumMileage / result.length).toFixed(2),
      avgEfficiency: +(sumEfficiency / result.length).toFixed(4),
      topPerformer: best
    };
  }, [batteriesData]);

  const batteriesWithEfficiency = useMemo(() => {
    if (!batteriesData?.data) return [];
    
    return batteriesData.data.map(battery => {
      const spentPercent = 100 - battery.chargeLevel;
      const efficiency = spentPercent > 0 ? battery.mileageSinceCharge / spentPercent : 0;
      return {
        ...battery,
        efficiency
      };
    }).sort((a, b) => (b.efficiency || 0) - (a.efficiency || 0));
  }, [batteriesData]);

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <Typography variant="body2" color="textSecondary">{t('batteries.totalBatteries')}</Typography>
            <Typography variant="h4">{stats.total}</Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <Typography variant="body2" color="textSecondary">{t('batteries.avgMileage')}</Typography>
            <Typography variant="h4">{stats.avgMileage} {t('batteries.unitKm')}</Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <Typography variant="body2" color="textSecondary">{t('batteries.avgEfficiency')}</Typography>
            <Typography variant="h4">{stats.avgEfficiency} {t('batteries.unitKmPercent')}</Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <TrendingUp color="success" />
              <Typography variant="body2" color="textSecondary">{t('batteries.topPerformer')}</Typography>
            </Box>
            {stats.topPerformer ? (
              <>
                <Typography variant="h6">{stats.topPerformer.name}</Typography>
                <Typography variant="body2" color="success.main">
                  {stats.topPerformer.efficiency?.toFixed(4)} {t('batteries.unitKmPercent')}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="textSecondary">-</Typography>
            )}
          </StatCard>
        </Grid>
      </Grid>

      <TableContainer component={Paper} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : batteriesWithEfficiency.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography variant="body1" color="textSecondary">
              {t('batteries.noData')}
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t('batteries.colName')}</TableHeaderCell>
                <TableHeaderCell>{t('batteries.colCode')}</TableHeaderCell>
                <TableHeaderCell>{t('batteries.colType')}</TableHeaderCell>
                <TableHeaderCell>{t('batteries.colHealth')}</TableHeaderCell>
                <TableHeaderCell>{t('batteries.colCycles')}</TableHeaderCell>
                <TableHeaderCell>{t('batteries.colMileageSinceCharge')}</TableHeaderCell>
                <TableHeaderCell>{t('batteries.colEfficiency')}</TableHeaderCell>
                <TableHeaderCell>{t('batteries.colInstallDate')}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {batteriesWithEfficiency.map((battery, index) => (
                <StyledTableRow key={battery._id}>
                  <StyledTableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {index < 3 && (
                        <Chip
                          label={`#${index + 1}`}
                          size="small"
                          color={index === 0 ? 'success' : index === 1 ? 'info' : 'warning'}
                        />
                      )}
                      {battery.name}
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>{battery.code}</StyledTableCell>
                  <StyledTableCell>
                    {battery.type === 'REPLACEABLE' ? t('batteries.typeReplaceable') : t('batteries.typeNonReplaceable')}
                  </StyledTableCell>
                  <StyledTableCell>
                    <Chip
                      label={`${battery.healthLevel}%`}
                      size="small"
                      color={battery.healthLevel >= 80 ? 'success' : battery.healthLevel >= 60 ? 'warning' : 'error'}
                    />
                  </StyledTableCell>
                  <StyledTableCell>{battery.cycles}</StyledTableCell>
                  <StyledTableCell>{battery.mileageSinceCharge.toFixed(2)} {t('batteries.unitKm')}</StyledTableCell>
                  <StyledTableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <BatteryChargingFull
                        fontSize="small"
                        color={battery.efficiency! >= stats.avgEfficiency ? 'success' : 'disabled'}
                      />
                      <Typography
                        color={battery.efficiency! >= stats.avgEfficiency ? 'success.main' : 'text.secondary'}
                        fontWeight={battery.efficiency! >= stats.avgEfficiency ? 600 : 400}
                      >
                        {battery.efficiency?.toFixed(4)} {t('batteries.unitKmPercent')}
                      </Typography>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                    {battery.installDate ? moment(battery.installDate).format('DD.MM.YYYY') : '-'}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default EfficiencyTab;
